import { useEffect, useState } from 'react'
import { Form, Link, useLocation } from 'react-router'
import { Input } from '#app/components/ui/input.tsx'
import { cn } from '#app/utils/misc.tsx'
import {
	SECTIONS,
	isPageActive,
	isSectionActive,
} from '#app/utils/site-sections.ts'

/**
 * Mobile primary navigation. Hidden on `md` and up — desktop uses SiteNav.
 *
 * Visual: hamburger button in the header. Tap → full-height panel slides in
 * from the right with backdrop. Panel shows search at top, every section
 * with all its children expanded (the site isn't huge — no need for
 * accordions / pinch interactions), and big touch targets (≥48px).
 *
 * Behaviour:
 * - Auto-closes on route change (so tapping a link "just works").
 * - Escape key closes.
 * - Body scroll is locked while open.
 * - Backdrop tap closes.
 */
export function MobileNav() {
	const [open, setOpen] = useState(false)
	const { pathname } = useLocation()

	// Close whenever the user navigates.
	useEffect(() => {
		setOpen(false)
	}, [pathname])

	// Body scroll lock + Escape to close, while open.
	useEffect(() => {
		if (!open) return
		const previousOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		window.addEventListener('keydown', onKey)
		return () => {
			document.body.style.overflow = previousOverflow
			window.removeEventListener('keydown', onKey)
		}
	}, [open])

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				aria-label="Avaa valikko"
				aria-expanded={open}
				className="text-foreground hover:bg-muted -mr-1 inline-flex size-11 items-center justify-center lg:hidden"
			>
				<HamburgerIcon />
			</button>

			{open ? (
				<div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Päävalikko">
					{/* Backdrop */}
					<button
						type="button"
						aria-label="Sulje valikko"
						onClick={() => setOpen(false)}
						className="bg-foreground/50 animate-in fade-in absolute inset-0 backdrop-blur-sm duration-200"
					/>

					{/* Panel */}
					<div className="bg-background border-border animate-in slide-in-from-right absolute inset-y-0 right-0 flex w-full max-w-sm flex-col border-l shadow-2xl duration-200">
						<header className="border-border flex items-center justify-between border-b px-4 py-3">
							<Link
								to="/"
								className="text-foreground text-base font-bold tracking-tight"
							>
								Frisbeegolfradat
							</Link>
							<button
								type="button"
								onClick={() => setOpen(false)}
								aria-label="Sulje valikko"
								className="text-foreground hover:bg-muted inline-flex size-11 items-center justify-center"
							>
								<CloseIcon />
							</button>
						</header>

						<div className="flex-1 overflow-y-auto">
							<div className="px-4 pt-5 pb-2">
								<Form method="get" action="/radat/haku" className="flex gap-2">
									<Input
										type="search"
										name="q"
										placeholder="Hae rataa tai kaupunkia…"
										aria-label="Hae rataa tai kaupunkia"
										className="h-12 flex-1 text-base"
									/>
									<button
										type="submit"
										className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-12 items-center justify-center px-4 text-sm font-semibold"
									>
										Hae
									</button>
								</Form>
							</div>

							<nav aria-label="Päävalikko" className="px-2 pt-2 pb-8">
								{SECTIONS.map((section) => {
									const sectionActive = isSectionActive(pathname, section)
									return (
										<div key={section.to} className="border-border border-b py-2 last:border-b-0">
											<Link
												to={section.to}
												className={cn(
													'flex items-center justify-between px-2 py-3 text-lg font-bold tracking-tight transition-colors',
													sectionActive
														? 'text-primary'
														: 'text-foreground hover:text-primary',
												)}
											>
												<span>{section.label}</span>
												<span
													aria-hidden
													className="text-muted-foreground text-xs"
												>
													→
												</span>
											</Link>
											{section.pages.length > 0 ? (
												<ul className="mt-1 mb-2 pl-3">
													{section.pages.map((page) => {
														const active = isPageActive(pathname, page)
														return (
															<li key={page.to}>
																<Link
																	to={page.to}
																	aria-current={active ? 'page' : undefined}
																	className={cn(
																		'flex items-center gap-3 px-2 py-3 text-base transition-colors',
																		active
																			? 'text-primary font-semibold'
																			: 'text-foreground/85 hover:text-primary',
																	)}
																>
																	<span
																		aria-hidden
																		className={cn(
																			'inline-block w-2 shrink-0 font-mono',
																			active
																				? 'text-primary'
																				: 'text-transparent',
																		)}
																	>
																		▸
																	</span>
																	<span>{page.label}</span>
																</Link>
															</li>
														)
													})}
												</ul>
											) : null}
										</div>
									)
								})}
							</nav>
						</div>
					</div>
				</div>
			) : null}
		</>
	)
}

function HamburgerIcon() {
	return (
		<svg
			width="22"
			height="22"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.25"
			strokeLinecap="round"
			aria-hidden
		>
			<line x1="4" y1="7" x2="20" y2="7" />
			<line x1="4" y1="12" x2="20" y2="12" />
			<line x1="4" y1="17" x2="20" y2="17" />
		</svg>
	)
}

function CloseIcon() {
	return (
		<svg
			width="22"
			height="22"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.25"
			strokeLinecap="round"
			aria-hidden
		>
			<line x1="6" y1="6" x2="18" y2="18" />
			<line x1="18" y1="6" x2="6" y2="18" />
		</svg>
	)
}
